/**
 * CalendarIntelligenceTab.jsx — the production Calendar (default, no flag).
 *
 * House style (white/gold/black, Dashboard-matched glass). Aggregates ONLY real,
 * dated sources available client-side:
 *   - Finance entries (due_date)
 *   - Vendors (deposit_due_date, balance_due_date, final_count_date, cancellation_deadline)
 *   - RSVP deadline (eventConfig.rsvp_deadline — exposed by GET /event/config)
 *   - Manual events / Personal blocks (calendar_events; event_type schedule|unavailable)
 *   - Event day (eventConfig.date)
 * Tasks / Program / Seating are intentionally NOT shown — they hold no dated data.
 *
 * Writes: manual calendar events only (create/edit/delete + share toggle), each
 * behind an explicit click; deletes confirm first; backend write endpoints are
 * operator-auth-gated. Smart Capture creates a Calendar task only (no cross-module
 * writes). The deterministic runway/conflict/lead-time intelligence is computed
 * client-side in calendarRunway.js.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { computeRunway, categorize, toDate } from './calendarRunway';
import { parseCapture } from './captureParse';
import federalHolidays, { holidayContext } from './federalHolidays';

const API = process.env.REACT_APP_BACKEND_URL;
const BG = '/calendar-bg.png';
const LOGO = '/mvitations-logo.png';

const SRC = {
  finance: '#B07A28', vendors: '#2A8A7A', rsvp: '#3E6DB5',
  manual: '#C6A75A', personal: '#5B5B5B', event: '#A8863C', holiday: '#9C6B3F',
};
const SRC_LABEL = { finance: 'Finance', vendors: 'Vendors', rsvp: 'RSVP', manual: 'Your dates', personal: 'Personal', event: 'Event day', holiday: 'Holiday' };
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const sMonth = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const fmtShort = (d) => d ? `${sMonth[d.getMonth()]} ${d.getDate()}` : '';
const fmtLong = (d) => d ? `${DOW[d.getDay()]}, ${sMonth[d.getMonth()]} ${d.getDate()}` : '';
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
const money = (n) => (n || n === 0) ? `$${Number(n).toLocaleString()}` : '';

const CSS = `
.ci-root{--ink:#0E0E0E;--ink-2:#3A352C;--ink-3:#6E675B;--black:#0E0E0E;--line:rgba(14,14,14,.14);--grid:rgba(14,14,14,.16);
  --gold:#C6A75A;--gold-deep:#A8863C;--gold-border:rgba(198,167,90,.42);--gold-soft:rgba(198,167,90,.14);--alert:#B0432F;
  --gold-div:linear-gradient(90deg,#D4AF37 0%,#F5D061 30%,rgba(212,175,55,.35) 60%,transparent 100%);
  --glass:rgba(255,255,255,.18);--glass-2:rgba(255,255,255,.18);--glass-bd:rgba(255,255,255,.28);
  --clear:rgba(255,255,255,.22);--clear-bd:rgba(255,255,255,.35);
  --depth:0 1px 0 rgba(255,255,255,.98) inset,0 2px 1px rgba(255,255,255,.6) inset,0 34px 64px -34px rgba(18,20,36,.60),0 12px 26px -12px rgba(18,20,36,.26),0 3px 7px -4px rgba(18,20,36,.16);
  --depth-sm:0 1px 0 rgba(255,255,255,.95) inset,0 12px 26px -16px rgba(18,20,36,.46),0 4px 9px -5px rgba(18,20,36,.18);
  --playfair:'Playfair Display',Georgia,serif;--cormorant:'Cormorant Garamond',Georgia,serif;--sys:system-ui,-apple-system,'Segoe UI',sans-serif;
  position:relative;font-family:var(--sys);color:var(--ink);padding:6px 4px 40px;min-height:80vh;
  background:#FFFFFF url("${BG}") center top / cover fixed no-repeat;
  /* full-bleed: cancel the admin content wrapper's 28px padding so the gold-geometric bg reaches every edge (no sphere peeking); square corners so no parent shows through */
  margin:-28px;border-radius:0 !important;-webkit-font-smoothing:antialiased}
.ci-root *{box-sizing:border-box}
.ci-veil{position:absolute;inset:0;z-index:0;pointer-events:none;background:radial-gradient(120% 90% at 50% 0%,rgba(255,255,255,.40),rgba(255,255,255,.22) 60%,rgba(255,255,255,.46) 100%)}
.ci-wrap{position:relative;z-index:1;max-width:1280px;margin:0 auto;padding:22px 26px}
.ci-serif{font-family:var(--playfair)}
.ci-hdr{display:flex;align-items:center;justify-content:space-between;gap:22px;padding-top:2px}
.ci-eyebrow{font-family:var(--cormorant);font-size:13px;letter-spacing:.26em;text-transform:uppercase;color:var(--ink-2);font-weight:700}
.ci-h1{font-family:var(--playfair);font-weight:700;font-size:clamp(26px,3.4vw,34px);line-height:1.1;letter-spacing:-.01em;margin:3px 0 0;color:var(--ink)}
.ci-sub{font-family:var(--cormorant);font-weight:600;font-size:15px;color:var(--ink-2);margin:3px 0 0}
.ci-brand{height:72px;width:auto;display:block;flex:none;filter:drop-shadow(0 6px 14px rgba(18,20,36,.18))}
.ci-divider{height:2px;background:var(--gold-div);margin-top:13px;box-shadow:0 1px 4px rgba(212,175,55,.25)}
.ci-card{background:var(--glass);backdrop-filter:blur(28px) saturate(125%);-webkit-backdrop-filter:blur(28px) saturate(125%);border:1px solid var(--glass-bd);border-radius:18px;box-shadow:var(--depth)}
.ci-gold-top{border-top:2px solid var(--gold)}
.ci-pill{display:inline-flex;align-items:center;gap:7px;border:1px solid var(--line);background:rgba(255,255,255,.7);border-radius:999px;padding:6px 12px;font-size:12px;color:var(--ink-2);cursor:pointer}
.ci-link{background:none;border:0;font:inherit;font-size:12.5px;font-weight:700;color:var(--gold-deep);cursor:pointer;padding:4px 2px}
.ci-jump{display:inline-flex;align-items:center;gap:6px;border:1.5px solid var(--gold);background:linear-gradient(180deg,#fff,#FBF5E6);color:var(--gold-deep);border-radius:999px;padding:7px 14px;font-size:12.5px;font-weight:700;cursor:pointer;box-shadow:var(--depth-sm)}
.ci-jump:hover{box-shadow:0 0 0 3px var(--gold-soft),var(--depth-sm)}
.ci-hidden{display:none}
.ci-kpi{display:flex;align-items:stretch;margin:18px 0 16px;padding:4px 8px;position:relative;background:var(--glass-2);backdrop-filter:blur(28px);-webkit-backdrop-filter:blur(28px);border:1px solid var(--glass-bd);border-radius:14px;box-shadow:var(--depth)}
.ci-kpi::before{content:"";position:absolute;left:16px;right:16px;top:0;height:2px;background:var(--gold-div);border-radius:2px;opacity:.85}
.ci-kcell{flex:1;padding:14px 20px;min-width:0}
.ci-kcell.clk{cursor:pointer;border-radius:10px;transition:background .15s}
.ci-kcell.clk:hover{background:rgba(198,167,90,.12)}
.ci-kcell .k{font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:var(--ink-2);margin-bottom:5px}
.ci-kcell .v{font-family:var(--playfair);font-size:25px;font-weight:700;letter-spacing:-.5px;line-height:1;color:var(--ink)}
.ci-kcell .more{font-size:9px;color:var(--gold-deep);font-weight:700;margin-top:5px;letter-spacing:.04em}
.ci-kdiv{width:1px;background:linear-gradient(180deg,transparent,rgba(14,14,14,.16) 20%,rgba(14,14,14,.16) 80%,transparent);margin:10px 0}
.ci-kpanel{margin:-6px 0 16px;padding:14px 18px;border:1px solid var(--gold-border);border-top:2px solid var(--gold);border-radius:14px;background:rgba(255,255,255,.5);backdrop-filter:blur(28px);-webkit-backdrop-filter:blur(28px);box-shadow:var(--depth)}
.ci-kpanel .kh{display:flex;align-items:center;gap:8px;margin-bottom:6px}
.ci-kpanel .kh h4{font-family:var(--playfair);font-size:15px;margin:0;color:var(--ink)}
.ci-kpanel .kx{margin-left:auto;cursor:pointer;color:var(--ink-3);font-size:15px;line-height:1}
.ci-kpanel .why{font-size:11.5px;color:var(--ink-2);line-height:1.5;margin-bottom:8px}
.ci-kpanel .krow{display:flex;align-items:center;gap:9px;padding:7px 0;border-top:1px solid var(--line);font-size:12.5px}
.ci-kpanel .krow:first-of-type{border-top:0}
.ci-kpanel .krow .when{margin-left:auto;color:var(--ink-3);font-size:11.5px;white-space:nowrap}
.ci-kpanel .krow .open{margin-left:auto;font-size:10.5px;color:var(--gold-deep);background:var(--gold-soft);border:1px solid var(--gold-border);border-radius:6px;padding:3px 8px;cursor:pointer;font-weight:700}
.ci-cap{margin-bottom:16px;padding:15px 20px}
.ci-cap-top{display:flex;align-items:center;gap:13px}
.ci-mav{position:relative !important;inset:auto !important;width:40px;height:40px;border-radius:50%;flex:none;background:radial-gradient(circle at 33% 27%,#FBF1D2,#C6A75A 56%,#7d6128 100%);box-shadow:0 0 0 4px rgba(198,167,90,.18),0 8px 18px -6px rgba(18,20,36,.5),inset -3px -5px 9px rgba(120,90,30,.4),inset 3px 4px 7px rgba(255,255,255,.65)}
.ci-cap-h{font-family:var(--playfair);font-size:16px;font-weight:600}
.ci-cap-s{font-size:12px;color:var(--ink-2);margin-top:1px}
.ci-cap-in{display:flex;gap:10px;margin-top:12px}
.ci-cap-in input{flex:1;font:inherit;font-size:14px;color:var(--ink);padding:13px 16px;border-radius:12px;border:1.5px solid var(--gold-border);background:#fff;outline:none;box-shadow:inset 0 1px 3px rgba(18,20,36,.07)}
.ci-cap-in input:focus{border-color:var(--gold);box-shadow:0 0 0 3px var(--gold-soft),inset 0 1px 3px rgba(18,20,36,.07)}
.ci-go{background:var(--black);color:#F4E9C8;border:0;border-radius:12px;padding:0 18px;font:inherit;font-weight:600;font-size:13.5px;cursor:pointer}
.ci-interp{margin-top:13px;border:1px solid var(--gold-border);border-radius:14px;background:rgba(255,255,255,.82);overflow:hidden;box-shadow:var(--depth-sm)}
.ci-interp .read{padding:11px 14px;border-bottom:1px solid var(--line);font-size:12.5px}
.ci-interp .read b{color:var(--gold-deep)}
.ci-chgset{padding:11px 14px}
.ci-chgset .lab{font-size:10px;letter-spacing:.12em;text-transform:uppercase;color:var(--ink-3);font-weight:700;margin-bottom:8px}
.ci-chg{display:flex;align-items:flex-start;gap:9px;padding:6px 0;font-size:12px}
.ci-chg .tk{width:16px;height:16px;border-radius:50%;background:var(--gold-soft);color:var(--gold-deep);font-size:10px;display:flex;align-items:center;justify-content:center;flex:none;margin-top:1px}
.ci-chg .tag{margin-left:auto;font-size:10px;font-weight:600;color:#fff;padding:3px 8px;border-radius:6px;white-space:nowrap}
.ci-rev{padding:12px 14px;display:flex;flex-direction:column;gap:11px}
.ci-rev-2{display:grid;grid-template-columns:1fr 1fr;gap:11px}
.ci-rev-row{display:flex;flex-direction:column;gap:5px}
.ci-rev-lab{font-size:10px;letter-spacing:.1em;text-transform:uppercase;color:var(--ink-3);font-weight:700;display:flex;gap:7px;align-items:baseline}
.ci-rev-lab em{font-style:normal;text-transform:none;letter-spacing:0;font-weight:500;color:var(--gold-deep);font-size:11px}
.ci-rev-in{font:inherit;font-size:13.5px;color:var(--ink);background:#fff;border:1px solid var(--gold-border);border-radius:10px;padding:9px 11px;box-shadow:var(--depth-sm)}
.ci-rev-in:focus{outline:none;border-color:var(--gold-deep);box-shadow:0 0 0 3px var(--gold-soft)}
.ci-rev-note{font-size:11.5px;color:var(--ink-3);font-style:italic;padding-top:1px}
.ci-rev-warn{display:flex;gap:8px;align-items:flex-start;font-size:12.5px;line-height:1.45;color:#7A4A12;background:rgba(212,150,40,.12);border:1px solid rgba(212,150,40,.32);border-radius:10px;padding:9px 11px}
.ci-rev-warn span{color:#B0432F;font-weight:700;flex:none}
@media(max-width:640px){.ci-rev-2{grid-template-columns:1fr}}
.ci-cap-act{display:flex;gap:9px;align-items:center;padding:11px 14px;border-top:1px solid var(--line);background:rgba(255,255,255,.5)}
.ci-cap-act .note{margin-left:auto;font-size:11px;color:var(--ink-3)}
.ci-btn-gold{background:var(--black);color:#F4E9C8;border:0;border-radius:10px;padding:9px 16px;font:inherit;font-weight:600;font-size:13px;cursor:pointer}
.ci-btn-ghost{background:rgba(255,255,255,.7);color:var(--ink-2);border:1px solid var(--line);border-radius:10px;padding:9px 16px;font:inherit;font-size:13px;cursor:pointer}
.ci-btn-ghost.sm{padding:6px 12px;font-size:12px}
.ci-remind{margin-top:10px;padding:11px 12px;border:1px solid var(--gold-border);border-radius:12px;background:var(--gold-soft)}
.ci-remind .lab{font-size:12.5px;color:var(--ink);margin-bottom:8px}
.ci-remind .lab b{color:var(--gold-deep)}
.ci-remind .opts{display:flex;gap:8px;flex-wrap:wrap}
.ci-remind .note{font-size:11px;color:var(--ink-3);margin-top:7px}
.ci-cols{display:grid;grid-template-columns:1.62fr 1fr;gap:18px;align-items:start}
.ci-cols + .ci-cols{margin-top:18px}
.ci-cal{padding:18px 20px}
.ci-cal-head{display:flex;align-items:center;justify-content:space-between;margin-bottom:12px;gap:12px;flex-wrap:wrap}
.ci-cal-title{font-family:var(--playfair);font-size:20px;display:flex;align-items:center;gap:10px}
.ci-nav{display:flex;gap:6px}
.ci-nav button{width:28px;height:28px;border-radius:8px;border:1px solid var(--line);background:rgba(255,255,255,.7);color:var(--ink-2);cursor:pointer;font:inherit}
.ci-nav .today{width:auto;padding:0 11px;font-size:12px}
.ci-cal-right{display:flex;align-items:center;gap:10px;flex-wrap:wrap}
.ci-seg{display:flex;background:rgba(14,14,14,.06);border-radius:10px;padding:3px}
.ci-seg button{border:0;background:transparent;padding:6px 12px;border-radius:8px;font-size:12.5px;color:var(--ink-2);cursor:pointer;font:inherit}
.ci-seg button.on{background:#fff;color:var(--ink);box-shadow:var(--depth-sm);font-weight:600}
.ci-fbar{display:flex;align-items:center;gap:14px;flex-wrap:wrap;margin-bottom:12px}
.ci-legend{display:flex;flex-wrap:wrap;gap:13px;font-size:11.5px;color:var(--ink-2)}
.ci-dot{display:inline-block;width:8px;height:8px;border-radius:50%;flex:none}
.ci-addbtn{margin-left:auto;font-size:11.5px;color:var(--gold-deep);font-weight:700;cursor:pointer;border:1px solid var(--gold-border);background:var(--gold-soft);border-radius:8px;padding:5px 10px}
.ci-dow{display:grid;grid-template-columns:repeat(7,minmax(0,1fr));font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:var(--ink-3);margin-bottom:6px}
.ci-dow span{padding-left:2px}
.ci-month{display:grid;grid-template-columns:repeat(7,minmax(0,1fr));border:1px solid var(--grid);border-radius:14px;overflow:hidden;background:rgba(255,255,255,.55);box-shadow:0 1px 0 rgba(255,255,255,.9) inset,0 14px 30px -20px rgba(18,20,36,.4),0 4px 10px -6px rgba(18,20,36,.16)}
.ci-day{min-height:84px;min-width:0;border-right:1px solid var(--grid);border-bottom:1px solid var(--grid);padding:6px 8px;position:relative;overflow:hidden;background:linear-gradient(180deg,rgba(255,255,255,.5),rgba(255,255,255,.18));transition:background .15s,box-shadow .15s}
.ci-day:nth-child(7n){border-right:0}
.ci-day:hover{background:rgba(255,255,255,.75);box-shadow:inset 0 0 0 1px var(--gold-border)}
.ci-dnum{font-size:12px;color:var(--ink-2);font-weight:600}
.ci-day.dim{background:rgba(14,14,14,.035)}
.ci-day.dim .ci-dnum{opacity:.4}
.ci-day.today{background:radial-gradient(120% 120% at 30% 0%,rgba(212,175,55,.30),rgba(198,167,90,.14));box-shadow:inset 0 0 0 2px var(--gold),0 6px 16px -8px rgba(168,134,60,.6)}
.ci-day.today .ci-dnum{color:var(--gold-deep);font-weight:700}
.ci-day.conflict{box-shadow:inset 0 0 0 2px rgba(176,67,47,.55)}
.ci-ev{display:flex;align-items:center;gap:5px;font-size:10.5px;line-height:1.25;margin-top:4px;color:var(--ink);min-width:0;cursor:pointer}
.ci-ev .d{width:6px;height:6px;border-radius:50%;flex:none}
.ci-ev .evt{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;min-width:0}
.ci-more{font-size:10px;color:var(--ink-3);margin-top:2px}
.ci-flag{position:absolute;top:5px;right:6px;font-size:11px;color:var(--alert)}
/* week */
.ci-week{display:grid;grid-template-columns:repeat(7,minmax(0,1fr));border:1px solid var(--grid);border-radius:14px;overflow:hidden;background:rgba(255,255,255,.55)}
.ci-wcol{border-right:1px solid var(--grid);min-height:230px;padding:8px;min-width:0}
.ci-wcol:last-child{border-right:0}
.ci-wcol .wh{font-size:10px;text-transform:uppercase;letter-spacing:.06em;color:var(--ink-3);font-weight:700}
.ci-wcol .wd{font-family:var(--playfair);font-size:16px;color:var(--ink)}
.ci-wcol.today .wd{color:var(--gold-deep)}
.ci-wev{display:flex;gap:6px;align-items:flex-start;font-size:11px;margin-top:7px;cursor:pointer;line-height:1.3}
.ci-wev .d{width:7px;height:7px;border-radius:50%;flex:none;margin-top:3px}
/* agenda */
.ci-agenda{border:1px solid var(--grid);border-radius:14px;overflow:hidden;background:rgba(255,255,255,.55)}
.ci-ag-day{padding:10px 14px;border-bottom:1px solid var(--grid)}
.ci-ag-day:last-child{border-bottom:0}
.ci-ag-h{font-family:var(--playfair);font-size:13px;color:var(--ink);margin-bottom:6px}
.ci-ag-row{display:flex;align-items:center;gap:9px;font-size:12.5px;padding:4px 0;cursor:pointer}
.ci-ag-row .when{margin-left:auto;color:var(--ink-3);font-size:11.5px}
.ci-empty{padding:30px;text-align:center;color:var(--ink-3);font-size:13px}
/* right rail */
.ci-milo-head{display:flex;align-items:center;gap:11px;padding:16px 18px 0}
.ci-milo-head .nm{font-weight:700;font-size:15px}
.ci-milo-head .sub{font-size:12px;color:var(--ink-2)}
.ci-att{padding:12px 18px 4px;display:flex;flex-direction:column;gap:9px}
.ci-acard{border:1px solid var(--clear-bd);border-left:3px solid var(--gold);border-radius:0 12px 12px 0;padding:10px 12px;background:rgba(255,255,255,.72);box-shadow:var(--depth-sm)}
.ci-acard.alert{border-left-color:var(--alert)}
.ci-acard .tag{font-size:9.5px;letter-spacing:.1em;text-transform:uppercase;font-weight:700}
.ci-acard .t{font-size:13px;font-weight:700;margin:3px 0 0}
.ci-acard .x{font-size:11.5px;color:var(--ink-2);line-height:1.4;margin-top:2px}
.ci-acard .act{margin-top:8px;display:flex;gap:7px;flex-wrap:wrap}
.ci-mini{font-size:11px;border:1px solid var(--line);background:rgba(255,255,255,.7);border-radius:8px;padding:5px 9px;cursor:pointer;color:var(--ink-2)}
.ci-mini.gold{background:var(--black);border-color:var(--black);color:#F4E9C8;font-weight:600}
.ci-pulse{padding:6px 18px 16px}
.ci-pulse h4{font-size:10.5px;letter-spacing:.13em;text-transform:uppercase;color:var(--ink-3);margin:12px 0 6px;font-weight:700}
.ci-pl{display:flex;align-items:center;gap:9px;padding:7px 0;border-top:1px solid var(--line);font-size:12.5px;cursor:pointer}
.ci-pl .when{margin-left:auto;color:var(--ink-3);font-size:11.5px}
.ci-sec{display:flex;align-items:center;gap:10px;margin-bottom:2px}
.ci-sec .dot{width:8px;height:8px;border-radius:50%;background:var(--gold)}
.ci-sec h3{font-family:var(--playfair);font-weight:600;font-size:18px;margin:0;color:var(--ink)}
.ci-sec .chip{margin-left:auto;background:var(--gold-soft);border:1px solid var(--gold-border);color:var(--gold-deep);border-radius:999px;padding:4px 12px;font-size:12px;font-weight:700}
.ci-runway{padding:18px 20px}
.ci-tlwrap{overflow:hidden}
.ci-timeline{position:relative;height:116px;margin:10px 6px 0;min-width:520px}
.ci-tl-line{position:absolute;left:0;right:0;top:40px;height:3px;background:rgba(14,14,14,.12);border-radius:3px}
.ci-tl-fill{position:absolute;left:0;top:40px;height:3px;background:linear-gradient(90deg,var(--gold),var(--gold-deep));border-radius:3px}
.ci-node{position:absolute;top:0;transform:translateX(-50%);width:90px;text-align:center}
.ci-ball{width:15px;height:15px;border-radius:50%;margin:33px auto 0;border:2px solid #fff;box-shadow:0 3px 7px -2px rgba(18,20,36,.5),0 0 0 1px var(--line);cursor:pointer;position:relative;z-index:2;transition:transform .16s}
.ci-node.big .ci-ball{width:20px;height:20px;margin-top:30px;box-shadow:0 4px 10px -2px rgba(18,20,36,.5),0 0 0 4px var(--gold-soft)}
.ci-node.risk .ci-ball{box-shadow:0 3px 7px -2px rgba(18,20,36,.5),0 0 0 3px rgba(198,167,90,.35)}
.ci-node:hover .ci-ball{transform:scale(1.3)}
.ci-node .lab{font-size:10.5px;color:var(--ink-2);margin-top:9px;line-height:1.2;font-weight:600}
.ci-node .dt{font-size:9.5px;color:var(--ink-3);margin-top:1px}
.ci-tip{position:absolute;bottom:68px;left:50%;transform:translateX(-50%);width:168px;background:#fff;border:1px solid var(--gold-border);border-radius:11px;box-shadow:var(--depth);padding:9px 11px;text-align:left;opacity:0;pointer-events:none;transition:opacity .16s;z-index:6}
.ci-node:hover .ci-tip{opacity:1}
.ci-tip .tt{font-size:12px;font-weight:700}
.ci-tip .tx{font-size:11px;color:var(--ink-2);line-height:1.4;margin-top:2px}
.ci-tip .aff{font-size:10px;color:var(--gold-deep);margin-top:5px;font-weight:700}
.ci-rfoot{display:flex;align-items:center;gap:12px;margin-top:4px}
.ci-rfoot .rl{font-size:12px;color:var(--ink-3)}
.ci-ord{display:flex;gap:11px;align-items:flex-start;padding:9px 0;border-top:1px solid var(--line)}
.ci-ord .n{width:20px;height:20px;border-radius:50%;background:var(--black);color:#F4E9C8;font-size:11px;font-weight:700;display:flex;align-items:center;justify-content:center;flex:none;margin-top:1px}
.ci-ord .t{font-size:12.5px;font-weight:700}
.ci-ord .why{font-size:11.5px;color:var(--ink-2);line-height:1.45}
.ci-ord .tabtag{margin-left:auto;font-size:10px;color:var(--gold-deep);background:var(--gold-soft);border:1px solid var(--gold-border);border-radius:6px;padding:3px 8px;white-space:nowrap;font-weight:700;cursor:pointer}
.ci-cols.stretch{align-items:stretch}
.ci-rcol{display:flex;flex-direction:column;gap:18px;min-width:0}
.ci-filler{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:26px 22px;min-height:120px}
.ci-filler .fo{font-size:22px;color:var(--gold);opacity:.75;margin-bottom:8px}
.ci-filler .ft{font-family:var(--playfair);font-size:15px;color:var(--ink);margin-bottom:4px}
.ci-filler .fx{font-size:12px;color:var(--ink-3);line-height:1.5;max-width:250px}
.ci-side{display:flex;flex-direction:column;gap:18px;min-width:0}
.ci-sbox{padding:15px 17px}
.ci-sbox h3{font-family:var(--playfair);font-size:16px;margin:0;color:var(--ink)}
.ci-sbox .sub{font-size:11.5px;color:var(--ink-2);line-height:1.45;margin-top:3px}
.ci-me{display:flex;align-items:center;gap:9px;padding:8px 0;border-top:1px solid var(--line);font-size:12.5px}
.ci-me:first-of-type{border-top:0}
.ci-me .nm{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;cursor:pointer}
.ci-me .share{margin-left:auto;font-size:13px;color:var(--ink-3);cursor:pointer}
.ci-me .x{color:var(--ink-3);cursor:pointer;font-size:13px}
.ci-me-add{display:flex;align-items:center;gap:7px;margin-top:8px;font-size:12px;color:var(--gold-deep);font-weight:700;cursor:pointer}
.ci-ical{display:flex;align-items:center;gap:8px;border:1px solid var(--line);border-radius:10px;background:rgba(255,255,255,.65);padding:9px 11px;font-size:11px;color:var(--ink-2);margin:11px 0}
.ci-ical .u{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.ci-sync{display:flex;gap:8px;flex-wrap:wrap}
.ci-foot{margin-top:22px;text-align:center;font-size:11px;color:var(--ink-3)}
/* modal (popover + form) */
.ci-overlay{position:fixed;inset:0;z-index:1000;background:rgba(18,20,36,.45);backdrop-filter:blur(3px);display:flex;align-items:center;justify-content:center;padding:20px}
.ci-modal{width:380px;max-width:94vw;background:#fff;border:1px solid var(--gold-border);border-top:3px solid var(--gold);border-radius:16px;box-shadow:0 40px 80px -30px rgba(18,20,36,.6);overflow:hidden}
.ci-modal .mh{display:flex;align-items:center;gap:8px;padding:14px 16px;border-bottom:1px solid var(--line)}
.ci-modal .mh .badge{font-size:9.5px;text-transform:uppercase;letter-spacing:.1em;font-weight:700;color:#fff;padding:3px 8px;border-radius:6px}
.ci-modal .mh .x{margin-left:auto;cursor:pointer;color:var(--ink-3);font-size:16px}
.ci-modal .mb{padding:14px 16px}
.ci-modal .mt{font-family:var(--playfair);font-size:17px;color:var(--ink);margin-bottom:6px}
.ci-modal .row{font-size:12.5px;color:var(--ink-2);display:flex;gap:8px;padding:3px 0}
.ci-modal .row b{color:var(--ink)}
.ci-modal .mf{display:flex;gap:8px;padding:13px 16px;border-top:1px solid var(--line);flex-wrap:wrap}
.ci-field{display:block;margin-bottom:10px}
.ci-field label{display:block;font-size:10px;text-transform:uppercase;letter-spacing:.08em;color:var(--ink-3);font-weight:700;margin-bottom:4px}
.ci-field input,.ci-field select{width:100%;font:inherit;font-size:13px;padding:9px 11px;border:1px solid var(--line);border-radius:9px;background:#fff;outline:none}
.ci-field input:focus,.ci-field select:focus{border-color:var(--gold);box-shadow:0 0 0 3px var(--gold-soft)}
.ci-check{display:flex;align-items:center;gap:8px;font-size:12.5px;color:var(--ink-2);cursor:pointer}
@keyframes ciRise{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
.ci-reveal{animation:ciRise .6s cubic-bezier(.2,.7,.2,1) both}
@keyframes ciPulse{0%,100%{box-shadow:var(--depth-sm),0 0 0 0 rgba(176,67,47,0)}50%{box-shadow:var(--depth-sm),0 0 13px 1px rgba(176,67,47,.28)}}
.ci-acard.alert{animation:ciPulse 2.8s ease-in-out infinite}
@media (prefers-reduced-motion:reduce){.ci-reveal,.ci-acard.alert{animation:none}.ci-node:hover .ci-ball{transform:none}}
@media (max-width:1040px){.ci-cols{grid-template-columns:1fr}.ci-kpi{flex-wrap:wrap}.ci-kdiv{display:none}.ci-kcell{flex:1 1 42%;min-width:150px;border-bottom:1px solid var(--line)}}
@media (max-width:680px){.ci-root{margin:0}.ci-wrap{padding:16px 14px}.ci-brand{height:50px}.ci-cap-in{flex-direction:column}.ci-go{width:100%;padding:11px}.ci-cal-head{flex-direction:column;align-items:flex-start}.ci-cal-right{width:100%;justify-content:space-between}.ci-day{min-height:60px}.ci-tlwrap{overflow-x:auto}.ci-week{grid-template-columns:1fr;}.ci-wcol{border-right:0;border-bottom:1px solid var(--grid);min-height:0}}
`;

const EMPTY_FORM = { id: null, title: '', date: '', start_time: '', event_type: 'schedule', share_with_guests: false };

export default function CalendarIntelligenceTab({ vendors = [], financeEntries = [], eventConfig = {}, onNavigateToTab = () => {} }) {
  const [manualEvents, setManualEvents] = useState([]);
  const [view, setView] = useState('Month');
  const [capOpen, setCapOpen] = useState(false);
  const [capText, setCapText] = useState('');
  const [interp, setInterp] = useState(null);
  const [busy, setBusy] = useState(false);
  const [kpiOpen, setKpiOpen] = useState(null);
  const [popover, setPopover] = useState(null);   // selected event
  const [form, setForm] = useState(null);          // add/edit form state (null = closed)

  const ICS_URL = `${API}/api/calendar/feed.ics`;
  const WEBCAL_URL = ICS_URL.replace(/^https?:/, 'webcal:');

  const eventDate = useMemo(() => toDate(eventConfig?.date || eventConfig?.event_date) || null, [eventConfig]);
  const rsvpDate = useMemo(() => toDate(eventConfig?.rsvp_deadline || eventConfig?.rsvp_due_date) || null, [eventConfig]);
  const today = useMemo(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), n.getDate()); }, []);
  const [anchor, setAnchor] = useState(() => {
    const base = toDate(eventConfig?.date || eventConfig?.event_date) || new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  const loadManual = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/calendar/events?source=manual`);
      if (Array.isArray(res.data)) setManualEvents(res.data);
    } catch (e) { /* non-fatal */ }
  }, []);
  useEffect(() => { loadManual(); }, [loadManual]);

  // ── aggregate ONLY real, dated sources ──
  const allEvents = useMemo(() => {
    const out = [];
    (financeEntries || []).forEach((fe) => {
      if (fe.due_date && fe.status !== 'archived' && fe.cancelled !== true) out.push({
        id: `fin_${fe._id || fe.id}`, sourceId: fe._id || fe.id, source: 'finance',
        title: `${fe.label || fe.name || 'Payment'} due`, date: fe.due_date,
        amount: fe.contract_amount || fe.amount, note: fe.notes || undefined,
      });
    });
    (vendors || []).forEach((v) => {
      const id = v._id || v.id; const nm = v.company_name || v.name || 'Vendor';
      const add = (suffix, date, title, amount) => { if (date) out.push({ id: `ven_${id}_${suffix}`, sourceId: id, source: 'vendors', title, date, amount }); };
      add('dep', v.deposit_due_date, `${nm} deposit due`, v.deposit_amount);
      add('bal', v.balance_due_date, `${nm} balance due`, v.balance_amount || v.balance_due);
      add('cnt', v.final_count_date, `${nm} final headcount`);
      add('can', v.cancellation_deadline, `${nm} cancel deadline`);
    });
    if (rsvpDate) out.push({ id: 'rsvp_deadline', source: 'rsvp', title: 'RSVPs due', date: ymd(rsvpDate) });
    if (eventDate) out.push({ id: 'event_day', source: 'event', title: 'Event day', date: ymd(eventDate) });
    (manualEvents || []).forEach((m) => out.push({
      ...m, id: m.id || m._id, sourceId: m.id || m._id,
      source: (m.event_type === 'unavailable') ? 'personal' : 'manual', manual: true,
    }));
    // US federal holidays — auto-included (read-only) from now through the event
    // year so the host always sees them on the calendar and in the agenda.
    const yStart = today.getFullYear();
    const yEnd = (toDate(eventDate) || today).getFullYear();
    for (let y = yStart; y <= yEnd; y++) {
      federalHolidays(y).forEach((h) => out.push({
        id: `hol_${h.date}`, source: 'holiday', title: h.name, date: h.date, holiday: true, readonly: true,
      }));
    }
    return out;
  }, [financeEntries, vendors, manualEvents, rsvpDate, eventDate, today]);

  // sources actually present (drives the legend — only real, dated, plumbed sources)
  const activeSources = useMemo(() => {
    const present = new Set(allEvents.map((e) => e.source));
    return ['finance', 'vendors', 'rsvp', 'manual', 'personal', 'holiday'].filter((s) => present.has(s));
  }, [allEvents]);

  const items = useMemo(() => (vendors || []).map((v) => ({
    title: v.category || v.name || 'Vendor', category: categorize(v.category || v.name),
    status: (v.status === 'booked' || v.status === 'confirmed' || v.deposit_paid) ? 'done' : 'open',
  })), [vendors]);

  const runway = useMemo(() => computeRunway(today, eventDate, { items, events: allEvents }), [today, eventDate, items, allEvents]);

  const eventsByDay = useMemo(() => {
    const map = {};
    allEvents.forEach((e) => { const d = toDate(e.date); if (!d) return; (map[ymd(d)] = map[ymd(d)] || []).push(e); });
    return map;
  }, [allEvents]);

  const conflictDays = useMemo(() => { const s = new Set(); (runway.conflicts || []).forEach((c) => s.add(c.date)); return s; }, [runway]);

  const pulse = useMemo(() => {
    const end = new Date(today.getTime() + 7 * 86400000);
    return allEvents.filter((e) => { const d = toDate(e.date); return d && d >= today && d <= end && e.status !== 'cancelled'; })
      .sort((a, b) => toDate(a.date) - toDate(b.date)).slice(0, 6);
  }, [allEvents, today]);

  const monthCells = useMemo(() => {
    const y = anchor.getFullYear(), m = anchor.getMonth();
    const firstDow = new Date(y, m, 1).getDay();
    const days = new Date(y, m + 1, 0).getDate();
    const arr = [];
    for (let i = 0; i < firstDow; i++) arr.push(null);
    for (let n = 1; n <= days; n++) arr.push(new Date(y, m, n));
    return arr;
  }, [anchor]);

  const weekDays = useMemo(() => {
    const start = new Date(anchor); start.setDate(anchor.getDate() - anchor.getDay());
    return Array.from({ length: 7 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  }, [anchor]);

  const agenda = useMemo(() => {
    const upcoming = allEvents.filter((e) => { const d = toDate(e.date); return d && d >= today; })
      .sort((a, b) => toDate(a.date) - toDate(b.date));
    const groups = [];
    let cur = null;
    upcoming.forEach((e) => { const k = ymd(toDate(e.date)); if (!cur || cur.key !== k) { cur = { key: k, d: toDate(e.date), items: [] }; groups.push(cur); } cur.items.push(e); });
    return groups.slice(0, 30);
  }, [allEvents, today]);

  const shiftNav = (dir) => {
    setAnchor((a) => view === 'Week'
      ? new Date(a.getFullYear(), a.getMonth(), a.getDate() + dir * 7)
      : new Date(a.getFullYear(), a.getMonth() + dir, 1));
  };
  const goToday = () => setAnchor(view === 'Week' ? new Date(today) : new Date(today.getFullYear(), today.getMonth(), 1));

  // ── smart capture: Calendar task ONLY (no cross-module writes) ──
  // Parse the host's own words into an EDITABLE draft. If they typed a date/time
  // we keep it verbatim; only when no date is given do we fall back to the
  // category lead-time safe start. Milo's title is a suggestion — fully editable
  // below before anything saves.
  const runCapture = () => {
    const text = capText.trim(); if (!text) { setInterp(null); return; }
    setInterp(parseCapture(text, { today, eventDate }));
  };
  const setInterpField = (k, v) => setInterp((p) => (p ? { ...p, [k]: v } : p));
  const confirmCapture = async () => {
    if (!interp) return;
    if (!interp.title.trim() || !interp.date) { toast.error('Add a title and a date.'); return; }
    setBusy(true);
    try {
      const keepNote = interp.detail && interp.detail.trim() !== interp.title.trim();
      await axios.post(`${API}/api/calendar/events`, {
        title: interp.title.trim(),
        date: interp.date,
        start_time: interp.time || null,
        event_type: 'schedule',
        note: keepNote ? `From your note: “${interp.detail}”` : null,
        source: 'manual',
      });
      toast.success('Added to your calendar.');
      setInterp(null); setCapText(''); setCapOpen(false); loadManual();
    } catch (e) {
      toast.error(e?.response?.status === 401 ? 'Please sign in again to save.' : 'Could not save — please try again.');
    } finally { setBusy(false); }
  };

  // ── manual event create / edit / delete / share (all explicit, authed) ──
  const openAdd = () => setForm({ ...EMPTY_FORM, date: ymd(today) });
  const openEdit = (e) => setForm({ id: e.id, title: e.title, date: e.date, start_time: e.start_time || '', event_type: e.event_type || (e.source === 'personal' ? 'unavailable' : 'schedule'), share_with_guests: !!e.share_with_guests });
  const saveForm = async () => {
    if (!form.title.trim() || !form.date) { toast.error('Add a title and date.'); return; }
    setBusy(true);
    const payload = { title: form.title.trim(), date: form.date, start_time: form.start_time || null, event_type: form.event_type, share_with_guests: form.event_type === 'unavailable' ? false : form.share_with_guests };
    try {
      if (form.id) await axios.put(`${API}/api/calendar/events/${form.id}`, payload);
      else await axios.post(`${API}/api/calendar/events`, { ...payload, source: 'manual' });
      toast.success(form.id ? 'Updated.' : 'Added.');
      setForm(null); setPopover(null); loadManual();
    } catch (e) {
      toast.error(e?.response?.status === 401 ? 'Please sign in again to save.' : 'Could not save.');
    } finally { setBusy(false); }
  };
  const deleteManual = async (id) => {
    if (!window.confirm('Remove this date from your calendar?')) return;
    setBusy(true);
    try { await axios.delete(`${API}/api/calendar/events/${id}`); toast.success('Removed.'); setPopover(null); loadManual(); }
    catch (e) { toast.error(e?.response?.status === 401 ? 'Please sign in again.' : 'Could not remove it.'); }
    finally { setBusy(false); }
  };
  const toggleShare = async (e) => {
    setBusy(true);
    try { await axios.put(`${API}/api/calendar/events/${e.id}`, { share_with_guests: !e.share_with_guests }); loadManual(); setPopover((p) => p ? { ...p, share_with_guests: !p.share_with_guests } : p); }
    catch (err) { toast.error(err?.response?.status === 401 ? 'Please sign in again.' : 'Could not update sharing.'); }
    finally { setBusy(false); }
  };

  // Opt-in reminder ahead of a vendor due date. The due date itself is already
  // on the calendar (derived from Vendors); this drops a manual "Reminder:" event
  // N days earlier so the host gets a nudge with runway to act. Explicit click only.
  const addVendorReminder = async (ev, daysBefore) => {
    const due = toDate(ev?.date);
    if (!due) return;
    const when = new Date(due.getTime() - daysBefore * 86400000);
    setBusy(true);
    try {
      await axios.post(`${API}/api/calendar/events`, {
        title: `Reminder: ${ev.title}`,
        date: ymd(when),
        event_type: 'schedule',
        note: `${ev.title} is due ${fmtLong(due)} — ${daysBefore} day${daysBefore === 1 ? '' : 's'} after this reminder.`,
        source: 'manual',
      });
      toast.success(`Reminder set for ${fmtShort(when)}.`);
      setPopover(null); loadManual();
    } catch (e) {
      toast.error(e?.response?.status === 401 ? 'Please sign in again to save.' : 'Could not set the reminder.');
    } finally { setBusy(false); }
  };

  const onEventClick = (e) => setPopover(e);
  const popAction = (e) => {
    if (e.source === 'finance') { onNavigateToTab('finance', e); setPopover(null); }
    else if (e.source === 'vendors') { onNavigateToTab('vendors', e); setPopover(null); }
  };

  const nodes = useMemo(() => {
    const out = [{ left: 7, big: true, color: 'var(--gold)', lab: 'Now', dt: fmtShort(today), tip: { tt: 'You are here', tx: `${runway.daysOut != null ? runway.daysOut : '—'} days out, ${runway.readinessPct}% ready.` } }];
    const cp = (runway.criticalPath || []).slice(0, 4);
    cp.forEach((r, i) => {
      const left = 26 + i * ((eventDate ? 58 : 64) / Math.max(1, cp.length));
      out.push({ left, risk: true, color: SRC.vendors, lab: (r.category || 'task'), dt: `by ${fmtShort(toDate(r.last_safe_start))}`,
        tip: { tt: `Start ${r.category}`, tx: `~${r.lead_days}-day lead. Last safe start ${fmtShort(toDate(r.last_safe_start))}.`, aff: `${r.severity} · lead-time` } });
    });
    if (eventDate) out.push({ left: 92, big: true, color: 'var(--gold)', lab: 'Event day', dt: fmtShort(eventDate), tip: { tt: 'The big day', tx: 'Everything points here.' }, right: true });
    return out;
  }, [runway, today, eventDate]);

  const readiness = runway.readinessPct;
  const fillPct = Math.max(2, Math.min(100, readiness));
  const personalEvents = useMemo(() => (manualEvents || []).map((m) => ({ ...m, id: m.id || m._id })), [manualEvents]);

  // status styling parity with the old CalendarTab: cancelled → strike + dim, paused → dim
  const mutedStyle = (e) => (e && (e.status === 'cancelled' || e.status === 'paused'))
    ? { opacity: 0.5, textDecoration: e.status === 'cancelled' ? 'line-through' : 'none' }
    : undefined;
  const Ev = (e, cls = 'ci-ev') => (
    <div className={cls} key={e.id} onClick={() => onEventClick(e)} title={e.title} style={mutedStyle(e)}>
      <span className="d" style={{ background: SRC[e.source] || SRC.manual }} />
      <span className="evt">{e.title}</span>
    </div>
  );

  return (
    <div className="ci-root">
      <style dangerouslySetInnerHTML={{ __html: CSS }} />
      <div className="ci-veil" />
      <div className="ci-wrap">

        <div className="ci-hdr ci-reveal">
          <div>
            <div className="ci-eyebrow">Planning</div>
            <h1 className="ci-h1 ci-serif">Calendar Intelligence</h1>
            <div className="ci-sub">Every date, deadline &amp; lead time in one place.</div>
          </div>
          <img className="ci-brand" src={LOGO} alt="M-Vitations" />
        </div>
        <div className="ci-divider ci-reveal" />

        {/* KPI strip — clickable drill-downs */}
        <div className="ci-kpi ci-reveal">
          <div className="ci-kcell"><div className="k">Runway to event</div><div className="v">{runway.daysOut != null ? `${runway.daysOut} days` : '—'}</div><div className="more">{readiness}% ready</div></div>
          <div className="ci-kdiv" />
          <div className="ci-kcell clk" role="button" tabIndex={0} onClick={() => setKpiOpen(kpiOpen === 'due' ? null : 'due')}><div className="k">Due this week</div><div className="v">{pulse.length}</div><div className="more">{pulse.length ? 'see what ▾' : 'nothing due'}</div></div>
          <div className="ci-kdiv" />
          <div className="ci-kcell clk" role="button" tabIndex={0} onClick={() => setKpiOpen(kpiOpen === 'conflicts' ? null : 'conflicts')}><div className="k">Conflicts</div><div className="v" style={{ color: runway.conflictCount ? 'var(--alert)' : 'var(--ink)' }}>{runway.conflictCount}</div><div className="more">{runway.conflictCount ? 'see which ▾' : 'all clear'}</div></div>
          <div className="ci-kdiv" />
          <div className="ci-kcell clk" role="button" tabIndex={0} onClick={() => setKpiOpen(kpiOpen === 'risks' ? null : 'risks')}><div className="k">Lead-time risks</div><div className="v" style={{ color: runway.leadTimeRiskCount ? 'var(--gold-deep)' : 'var(--ink)' }}>{runway.leadTimeRiskCount}</div><div className="more">what's this? ▾</div></div>
        </div>

        {kpiOpen && (
          <div className="ci-kpanel ci-reveal">
            <div className="kh"><h4>{kpiOpen === 'due' ? 'Due in the next 7 days' : kpiOpen === 'conflicts' ? 'Scheduling conflicts' : 'Lead-time risks'}</h4><span className="kx" onClick={() => setKpiOpen(null)}>✕</span></div>
            {kpiOpen === 'due' && (pulse.length ? pulse.map((e, i) => (
              <div className="krow" key={i}><span className="ci-dot" style={{ background: SRC[e.source] || SRC.manual }} /> {e.title}
                {(e.source === 'finance' || e.source === 'vendors') && <span className="open" onClick={() => onNavigateToTab(e.source === 'finance' ? 'finance' : 'vendors', e)}>Open {e.source === 'finance' ? 'Finance' : 'Vendors'}</span>}
                <span className="when">{fmtShort(toDate(e.date))}</span></div>
            )) : <div className="why">Nothing falls due in the next week.</div>)}
            {kpiOpen === 'conflicts' && (runway.conflicts.length ? (<>
              <div className="why"><b>✦ Milo:</b> these are booked at the same time. Open one and give it a new time — I'll leave the other where it is.</div>
              {runway.conflicts.map((c, i) => (
                <div className="krow" key={i} style={{ flexWrap: 'wrap' }}>
                  <span className="ci-dot" style={{ background: 'var(--alert)' }} />
                  <span style={{ fontWeight: 600 }}>{c.events.map((e) => e.title).join('  ⇄  ')}</span>
                  <span className="when">{c.time} · {c.date}</span>
                  <span style={{ display: 'flex', gap: 6, flexBasis: '100%', marginTop: 6, paddingLeft: 18, flexWrap: 'wrap' }}>
                    {c.events.map((e, j) => (
                      (e.manual || e.source === 'manual' || e.source === 'personal')
                        ? <span className="open" key={j} style={{ marginLeft: 0 }} onClick={() => { setKpiOpen(null); openEdit(e); }}>Reschedule “{e.title}”</span>
                        : <span className="open" key={j} style={{ marginLeft: 0 }} onClick={() => { setKpiOpen(null); onNavigateToTab(e.source, e); }}>Open {e.source === 'finance' ? 'Finance' : 'Vendors'}</span>
                    ))}
                  </span>
                </div>
              ))}
            </>) : <div className="why">No two timed items collide right now.</div>)}
            {kpiOpen === 'risks' && (<>
              <div className="why"><b>What this means:</b> some things take time to arrange — a videographer needs ~30 days, custom favors ~3 weeks. Milo counts backward from your event date to the <b>last safe day to start</b>. A risk means that day is here or past — start now to avoid rush fees.</div>
              {runway.risks.length ? runway.risks.map((r, i) => (
                <div className="krow" key={i}><span className="ci-dot" style={{ background: r.severity === 'overdue' ? 'var(--alert)' : 'var(--gold)' }} /> Start {r.category} — ~{r.lead_days}-day lead{r.severity === 'overdue' ? ', already overdue' : ''}<span className="open" onClick={() => onNavigateToTab('vendors')}>Open Vendors</span><span className="when">by {fmtShort(toDate(r.last_safe_start))}</span></div>
              )) : <div className="why" style={{ marginBottom: 0 }}>Nothing is at risk — every long-lead item is handled.</div>}
            </>)}
          </div>
        )}

        {/* SMART CAPTURE — Calendar task only */}
        <div className="ci-card ci-gold-top ci-cap ci-reveal">
          <div className="ci-cap-top">
            <div className="ci-mav" />
            <div style={{ flex: 1 }}><div className="ci-cap-h ci-serif">Tell me what needs to happen</div><div className="ci-cap-s">Type it the way you'd say it — I'll add it to your calendar with a smart due date.</div></div>
            <button className="ci-link" onClick={() => setCapOpen((o) => !o)}>{capOpen ? 'Hide ⌄' : 'Details ›'}</button>
          </div>
          <div className="ci-cap-in">
            <input value={capText} onChange={(e) => setCapText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (setCapOpen(true), runCapture())} placeholder="e.g. book a videographer · order favors · cake tasting Sat" />
            <button className="ci-go" onClick={() => { setCapOpen(true); runCapture(); }}>Capture →</button>
          </div>
          {capOpen && !interp && (
            <div className="ci-interp"><div className="read">Type a plan in your words and hit <b>Capture</b>. If you name a day or time, I’ll keep it exactly. If you don’t, I’ll work back from your event to a safe start. You review and edit everything before it saves — nothing changes until you confirm.</div></div>
          )}
          {capOpen && interp && (
            <div className="ci-interp">
              <div className="read">
                {interp.hasExplicitDate
                  ? <>Got it — here’s your note as a calendar entry. <b>Edit anything</b> before you save.</>
                  : <>No date in your note, so I worked back from your event: {interp.category === 'generic' ? <>about a <b>week</b>’s lead</> : <>a <b>~{interp.lead}-day</b> lead for {interp.category}</>} → a safe start of <b>{interp.date}</b>. Edit anything below.</>}
              </div>
              <div className="ci-rev">
                <label className="ci-rev-row">
                  <span className="ci-rev-lab">Title <em>Milo’s suggestion — edit freely</em></span>
                  <input className="ci-rev-in" value={interp.title} onChange={(e) => setInterpField('title', e.target.value)} placeholder="What needs to happen" />
                </label>
                <div className="ci-rev-2">
                  <label className="ci-rev-row">
                    <span className="ci-rev-lab">Date</span>
                    <input className="ci-rev-in" type="date" value={interp.date} onChange={(e) => setInterpField('date', e.target.value)} />
                  </label>
                  <label className="ci-rev-row">
                    <span className="ci-rev-lab">Time <em>optional</em></span>
                    <input className="ci-rev-in" type="time" value={interp.time || ''} onChange={(e) => setInterpField('time', e.target.value || null)} />
                  </label>
                </div>
                {interp.detail && interp.detail.trim() !== interp.title.trim() && (
                  <div className="ci-rev-note">I’ll keep your exact words in the entry’s notes: “{interp.detail}”</div>
                )}
                {(() => { const w = holidayContext(interp.date); return w ? (
                  <div className="ci-rev-warn"><span aria-hidden="true">⚠</span> {w.message}</div>
                ) : null; })()}
              </div>
              <div className="ci-cap-act"><button className="ci-btn-gold" disabled={busy} onClick={confirmCapture}>{busy ? 'Saving…' : 'Confirm & add to calendar'}</button><button className="ci-btn-ghost" onClick={() => setInterp(null)}>Cancel</button><span className="note">Calendar only · nothing changes until you confirm.</span></div>
            </div>
          )}
        </div>

        {/* ROW 1: calendar + Milo (right rail kept intentional with a filler card) */}
        <div className="ci-cols stretch">
          <div className="ci-card ci-gold-top ci-cal ci-reveal">
            <div className="ci-cal-head">
              <div className="ci-cal-title ci-serif">
                <div className="ci-nav"><button onClick={() => shiftNav(-1)}>‹</button><button onClick={() => shiftNav(1)}>›</button><button className="today" onClick={goToday}>Today</button></div>
                {view === 'Week' ? `${fmtShort(weekDays[0])} – ${fmtShort(weekDays[6])}` : `${MONTHS[anchor.getMonth()]} ${anchor.getFullYear()}`}
              </div>
              <div className="ci-cal-right">
                <button className="ci-jump" onClick={goToday}>⌖ Jump to date</button>
                <div className="ci-seg">{['Month', 'Week', 'Agenda'].map((v) => <button key={v} className={view === v ? 'on' : ''} onClick={() => setView(v)}>{v}</button>)}</div>
              </div>
            </div>
            <div className="ci-fbar">
              <div className="ci-legend">
                {activeSources.map((s) => <span key={s}><span className="ci-dot" style={{ background: SRC[s] }} /> {SRC_LABEL[s]}</span>)}
                {activeSources.length === 0 && <span style={{ color: 'var(--ink-3)' }}>No dated items yet</span>}
              </div>
              <span className="ci-addbtn" onClick={openAdd}>＋ Add date</span>
            </div>

            {view === 'Month' && (<>
              <div className="ci-dow">{DOW.map((d) => <span key={d}>{d}</span>)}</div>
              <div className="ci-month">
                {monthCells.map((d, i) => {
                  if (!d) return <div className="ci-day dim" key={`e${i}`} />;
                  const key = ymd(d); const evs = eventsByDay[key] || [];
                  const isToday = d.getTime() === today.getTime(); const isConflict = conflictDays.has(key);
                  return (
                    <div className={`ci-day${isToday ? ' today' : ''}${isConflict ? ' conflict' : ''}`} key={key}>
                      <div className="ci-dnum">{d.getDate()}</div>
                      {isConflict && <div className="ci-flag">⚑</div>}
                      {evs.slice(0, 2).map((e) => Ev(e))}
                      {evs.length > 2 && <div className="ci-more">+{evs.length - 2} more</div>}
                    </div>
                  );
                })}
              </div>
            </>)}

            {view === 'Week' && (
              <div className="ci-week">
                {weekDays.map((d) => {
                  const evs = eventsByDay[ymd(d)] || []; const isToday = d.getTime() === today.getTime();
                  return (
                    <div className={`ci-wcol${isToday ? ' today' : ''}`} key={ymd(d)}>
                      <div className="wh">{DOW[d.getDay()]}</div><div className="wd">{d.getDate()}</div>
                      {evs.map((e) => (
                        <div className="ci-wev" key={e.id} onClick={() => onEventClick(e)} style={mutedStyle(e)}><span className="d" style={{ background: SRC[e.source] || SRC.manual }} /><span>{e.title}{e.start_time ? ` · ${e.start_time}` : ''}</span></div>
                      ))}
                      {evs.length === 0 && <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 7 }}>—</div>}
                    </div>
                  );
                })}
              </div>
            )}

            {view === 'Agenda' && (
              <div className="ci-agenda">
                {agenda.length === 0 && <div className="ci-empty">Nothing scheduled ahead. Use “＋ Add date” or the capture bar above.</div>}
                {agenda.map((g) => (
                  <div className="ci-ag-day" key={g.key}>
                    <div className="ci-ag-h">{fmtLong(g.d)}{g.d.getTime() === today.getTime() ? ' · Today' : ''}</div>
                    {g.items.map((e) => (
                      <div className="ci-ag-row" key={e.id} onClick={() => onEventClick(e)} style={mutedStyle(e)}><span className="ci-dot" style={{ background: SRC[e.source] || SRC.manual }} /> {e.title}{e.start_time ? <span style={{ color: 'var(--ink-3)' }}> · {e.start_time}</span> : null}<span className="when">{SRC_LABEL[e.source] || ''}</span></div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="ci-rcol">
            <div className="ci-card ci-gold-top ci-reveal" style={{ paddingBottom: 4 }}>
              <div className="ci-milo-head"><div className="ci-mav" /><div><div className="nm">Milo</div><div className="sub">{(runway.risks || []).length + runway.conflictCount || 'No'} things worth a look</div></div></div>
              <div className="ci-att">
                {(runway.conflicts || []).map((c, i) => (
                  <div className="ci-acard alert" key={'c' + i}><div className="tag" style={{ color: 'var(--alert)' }}>Conflict · {c.date}</div><div className="t">{c.events.map((e) => e.title).join(' ⇄ ')}</div><div className="x">Two things at {c.time} — one has to move.</div><div className="act"><span className="ci-mini gold" onClick={() => setView('Week')}>View week</span></div></div>
                ))}
                {(runway.risks || []).slice(0, 3).map((r, i) => (
                  <div className="ci-acard" key={i}><div className="tag" style={{ color: 'var(--gold-deep)' }}>{r.severity === 'overdue' ? 'Overdue' : 'Lead-time risk'}</div><div className="t">Start {r.category} {r.severity === 'overdue' ? 'now' : 'this week'}</div><div className="x">~{r.lead_days}-day lead · safe start {fmtShort(toDate(r.last_safe_start))}.</div><div className="act"><span className="ci-mini gold" onClick={() => onNavigateToTab('vendors')}>Open Vendors</span></div></div>
                ))}
                {runway.conflictCount === 0 && (runway.risks || []).length === 0 && (<div className="ci-acard"><div className="t">You're on track</div><div className="x">No conflicts or lead-time risks right now.</div></div>)}
              </div>
              <div className="ci-pulse">
                <h4>Next 7 days</h4>
                {pulse.length === 0 && <div style={{ fontSize: 12, color: 'var(--ink-3)', padding: '6px 0' }}>Nothing in the next week.</div>}
                {pulse.map((e, i) => (<div className="ci-pl" key={i} onClick={() => onEventClick(e)}><span className="ci-dot" style={{ background: SRC[e.source] || SRC.manual }} /> {e.title} <span className="when">{fmtShort(toDate(e.date))}</span></div>))}
              </div>
            </div>
            {((runway.conflicts || []).length + Math.min((runway.risks || []).length, 3)) < 2 && (
              <div className="ci-card ci-filler ci-reveal">
                <div className="fo">✦</div>
                <div className="ft">All clear here</div>
                <div className="fx">When Milo spots anything else worth your attention, it'll appear in this space.</div>
              </div>
            )}
          </div>
        </div>

        {/* ROW 2: runway + side */}
        <div className="ci-cols">
          <div className="ci-card ci-gold-top ci-runway ci-reveal">
            <div className="ci-sec"><span className="dot" /><h3 className="ci-serif">The road to your event</h3><span className="chip">{runway.daysOut != null ? `${runway.daysOut} days` : '—'} · {readiness}% ready</span></div>
            <div className="ci-tlwrap"><div className="ci-timeline">
              <div className="ci-tl-line" /><div className="ci-tl-fill" style={{ width: `${fillPct}%` }} />
              {nodes.map((n, i) => (
                <div className={`ci-node${n.big ? ' big' : ''}${n.risk ? ' risk' : ''}`} key={i} style={{ left: `${n.left}%` }}>
                  <div className="ci-ball" style={{ background: n.color }} /><div className="lab">{n.lab}</div><div className="dt">{n.dt}</div>
                  <div className="ci-tip" style={n.right ? { left: 'auto', right: 0, transform: 'none' } : undefined}><div className="tt">{n.tip.tt}</div><div className="tx">{n.tip.tx}</div>{n.tip.aff && <div className="aff">{n.tip.aff}</div>}</div>
                </div>
              ))}
            </div></div>
            <div className="ci-rfoot"><div className="rl">Hover any point for detail.</div></div>
            {(runway.criticalPath || []).length > 0 && (
              <div style={{ marginTop: 6 }}>
                <div style={{ fontSize: 10.5, letterSpacing: '.13em', textTransform: 'uppercase', color: 'var(--ink-3)', fontWeight: 700, margin: '8px 0 0' }}>Do these first</div>
                {runway.criticalPath.map((r, i) => (
                  <div className="ci-ord" key={i}><div className="n">{i + 1}</div><div><div className="t">Start {r.category}</div><div className="why">~{r.lead_days}-day lead · safe start {fmtShort(toDate(r.last_safe_start))}{r.severity === 'overdue' ? ' · already overdue' : ''}.</div></div><span className="tabtag" onClick={() => onNavigateToTab('vendors')}>Vendors</span></div>
                ))}
              </div>
            )}
          </div>

          <div className="ci-side">
            <div className="ci-card ci-sbox ci-reveal">
              <div className="ci-sec" style={{ marginBottom: 8 }}><span className="dot" style={{ background: SRC.personal }} /><h3 className="ci-serif">Your own dates</h3></div>
              {personalEvents.length === 0 && <div style={{ fontSize: 12, color: 'var(--ink-3)', padding: '6px 0' }}>No personal dates yet.</div>}
              {personalEvents.slice(0, 6).map((m) => (
                <div className="ci-me" key={m.id}>
                  <span className="ci-dot" style={{ background: m.event_type === 'unavailable' ? SRC.personal : SRC.manual }} />
                  <span className="nm" onClick={() => onEventClick({ ...m, source: m.event_type === 'unavailable' ? 'personal' : 'manual', manual: true })}>{m.title}</span>
                  {m.event_type !== 'unavailable' && <span className="share" title={m.share_with_guests ? 'Shared with guests — click to make private' : 'Private — click to share'} onClick={() => toggleShare(m)}>{m.share_with_guests ? '👥' : '🔒'}</span>}
                  <span className="x" title="Remove" onClick={() => deleteManual(m.id)}>✕</span>
                </div>
              ))}
              <div className="ci-me-add" onClick={openAdd}>＋ Add a date or block time off</div>
            </div>

            <div className="ci-card ci-sbox ci-reveal">
              <div className="ci-sec" style={{ marginBottom: 4 }}><span className="dot" style={{ background: SRC.rsvp }} /><h3 className="ci-serif">On your phone</h3></div>
              <div className="sub">Subscribe once — it refreshes itself. Guests only see what you mark shared.</div>
              <div className="ci-ical"><span style={{ color: 'var(--gold-deep)' }}>🔗</span><span className="u">{ICS_URL}</span><span style={{ marginLeft: 'auto', color: 'var(--gold-deep)', cursor: 'pointer', fontWeight: 700 }} onClick={() => { try { navigator.clipboard.writeText(ICS_URL); toast.success('Feed URL copied.'); } catch (e) { toast.error('Copy not available here.'); } }}>copy</span></div>
              <div className="ci-sync">
                <span className="ci-pill" onClick={() => { try { window.open(WEBCAL_URL, '_blank'); } catch (e) {} }}>Apple</span>
                <span className="ci-pill" onClick={() => { try { window.open(`https://calendar.google.com/calendar/r?cid=${encodeURIComponent(WEBCAL_URL)}`, '_blank'); } catch (e) {} }}>Google</span>
                <span className="ci-pill" onClick={() => { try { window.open(`https://outlook.live.com/calendar/0/addfromweb?url=${encodeURIComponent(ICS_URL)}&name=${encodeURIComponent((eventConfig && eventConfig.event_name) || 'Event Calendar')}`, '_blank'); } catch (e) {} }}>Outlook</span>
              </div>
            </div>
          </div>
        </div>

        <div className="ci-foot">M·Vitations · Calendar Intelligence{eventConfig?.event_name ? ` · ${eventConfig.event_name}` : ''}</div>
      </div>

      {/* EVENT DETAIL POPOVER */}
      {popover && !form && (
        <div className="ci-overlay" onClick={() => setPopover(null)}>
          <div className="ci-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mh"><span className="badge" style={{ background: SRC[popover.source] || SRC.manual }}>{SRC_LABEL[popover.source] || 'Event'}</span><span className="x" onClick={() => setPopover(null)}>✕</span></div>
            <div className="mb">
              <div className="mt">{popover.title}</div>
              <div className="row"><span>📅</span><b>{fmtLong(toDate(popover.date))}</b>{popover.start_time ? <span> · {popover.start_time}</span> : null}</div>
              {popover.amount ? <div className="row"><span>💰</span>{money(popover.amount)}</div> : null}
              {popover.note ? <div className="row" style={{ fontStyle: 'italic' }}>{popover.note}</div> : null}
              {popover.source === 'rsvp' && <div className="row">The deadline for guests to respond.</div>}
              {popover.source === 'event' && <div className="row">Your event day.</div>}
              {popover.source === 'holiday' && <div className="row">US federal holiday — most stores, banks and vendors are closed or on holiday hours.</div>}
              {popover.source === 'vendors' && (
                <div className="ci-remind">
                  <div className="lab"><b>✦ Milo:</b> want a reminder before this is due?</div>
                  <div className="opts">
                    <button className="ci-btn-ghost sm" disabled={busy} onClick={() => addVendorReminder(popover, 7)}>1 week before</button>
                    <button className="ci-btn-ghost sm" disabled={busy} onClick={() => addVendorReminder(popover, 3)}>3 days before</button>
                  </div>
                  <div className="note">Adds a calendar reminder — only if you ask. Nothing else changes.</div>
                </div>
              )}
              {(() => { const w = popover.source !== 'holiday' && holidayContext(popover.date); return w ? (
                <div className="row" style={{ color: '#7A4A12' }}>⚠ {w.message}</div>
              ) : null; })()}
            </div>
            <div className="mf">
              {popover.source === 'finance' && <button className="ci-btn-gold" onClick={() => popAction(popover)}>Open in Finance</button>}
              {popover.source === 'vendors' && <button className="ci-btn-gold" onClick={() => popAction(popover)}>Open in Vendors</button>}
              {popover.manual && <>
                <button className="ci-btn-gold" onClick={() => openEdit(popover)}>Edit</button>
                {popover.source !== 'personal' && <button className="ci-btn-ghost" onClick={() => toggleShare(popover)}>{popover.share_with_guests ? 'Make private' : 'Share with guests'}</button>}
                <button className="ci-btn-ghost" onClick={() => deleteManual(popover.id)}>Delete</button>
              </>}
              {(popover.source === 'rsvp' || popover.source === 'event' || popover.source === 'holiday') && <button className="ci-btn-ghost" onClick={() => setPopover(null)}>Close</button>}
            </div>
          </div>
        </div>
      )}

      {/* ADD / EDIT FORM */}
      {form && (
        <div className="ci-overlay" onClick={() => setForm(null)}>
          <div className="ci-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mh"><span className="badge" style={{ background: SRC.manual }}>{form.id ? 'Edit date' : 'Add date'}</span><span className="x" onClick={() => setForm(null)}>✕</span></div>
            <div className="mb">
              <div className="ci-field"><label>What is it?</label><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Cake tasting" /></div>
              <div className="ci-field"><label>Date</label><input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
              <div className="ci-field"><label>Time (optional)</label><input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} /></div>
              <div className="ci-field"><label>Type</label><select value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })}><option value="schedule">Scheduled event</option><option value="unavailable">Block time off (private)</option></select></div>
              {form.event_type === 'schedule' && <label className="ci-check"><input type="checkbox" checked={form.share_with_guests} onChange={(e) => setForm({ ...form, share_with_guests: e.target.checked })} /> Share with guests</label>}
            </div>
            <div className="mf"><button className="ci-btn-gold" disabled={busy} onClick={saveForm}>{busy ? 'Saving…' : (form.id ? 'Save changes' : 'Add to calendar')}</button><button className="ci-btn-ghost" onClick={() => setForm(null)}>Cancel</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
